/**
 * Renuex Labs - Main JavaScript
 * Handles interactions, cart, and dynamic features
 */

(function () {
    'use strict';

    // ============================================
    // STICKY HEADER
    // ============================================
    function initStickyHeader() {
        const header = document.getElementById('site-header');
        if (!header) return;

        window.addEventListener('scroll', function () {
            if (window.scrollY > 50) {
                header.classList.add('scrolled');
            } else {
                header.classList.remove('scrolled');
            }
        });
    }

    // ============================================
    // SMOOTH SCROLL
    // ============================================
    function initSmoothScroll() {
        document.querySelectorAll('a[href^="#"]').forEach(anchor => {
            anchor.addEventListener('click', function (e) {
                const href = this.getAttribute('href');
                if (href === '#') return;

                e.preventDefault();
                const target = document.querySelector(href);
                if (target) {
                    target.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            });
        });
    }

    // ============================================
    // FADE IN ON SCROLL
    // ============================================
    function initScrollAnimations() {
        const observerOptions = {
            threshold: 0.1,
            rootMargin: '0px 0px -50px 0px'
        };

        const observer = new IntersectionObserver(function (entries) {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('fade-in');
                    observer.unobserve(entry.target);
                }
            });
        }, observerOptions);

        document.querySelectorAll('.card, .testimonial, .product-card').forEach(el => {
            observer.observe(el);
        });
    }

    // ============================================
    // CART FUNCTIONALITY
    // ============================================
    const Cart = {
        init: function () {
            this.drawer = document.getElementById('slide-cart-drawer');
            this.overlay = document.getElementById('cart-drawer-overlay');
            this.body = document.getElementById('cart-drawer-body');
            this.subtotal = document.getElementById('cart-drawer-subtotal');
            this.closeBtn = document.getElementById('close-cart-drawer');

            this.updateCartCount();
            this.bindEvents();
        },

        bindEvents: function () {
            // Add to cart forms
            document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
                form.addEventListener('submit', this.handleAddToCart.bind(this));
            });

            // Cart triggers
            document.querySelectorAll('.cart-trigger-elite, a[href="/cart"]').forEach(trigger => {
                trigger.addEventListener('click', (e) => {
                    e.preventDefault();
                    this.openDrawer();
                });
            });

            if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.closeDrawer());
            if (this.overlay) this.overlay.addEventListener('click', () => this.closeDrawer());
        },

        openDrawer: function () {
            if (!this.drawer) return;
            this.fetchCart();
            this.drawer.classList.add('active');
            this.overlay.classList.add('active');
            document.body.style.overflow = 'hidden';
        },

        closeDrawer: function () {
            if (!this.drawer) return;
            this.drawer.classList.remove('active');
            this.overlay.classList.remove('active');
            document.body.style.overflow = '';
        },

        handleAddToCart: function (e) {
            e.preventDefault();
            const form = e.target;
            const formData = new FormData(form);

            const submitBtn = form.querySelector('button[type="submit"]');
            const originalText = submitBtn ? submitBtn.innerHTML : '';
            if (submitBtn) submitBtn.innerHTML = 'Synthesizing...';

            fetch('/cart/add.js', {
                method: 'POST',
                body: formData
            })
                .then(response => response.json())
                .then(data => {
                    if (submitBtn) submitBtn.innerHTML = originalText;
                    this.updateCartCount();
                    this.openDrawer();
                })
                .catch(error => {
                    if (submitBtn) submitBtn.innerHTML = originalText;
                    this.showNotification('Error adding protocol.', 'error');
                    console.error('Error:', error);
                });
        },

        fetchCart: function () {
            fetch('/cart.js')
                .then(res => res.json())
                .then(cart => {
                    this.renderCart(cart);
                });
        },

        renderCart: function (cart) {
            if (!this.body) return;
            const formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency: cart.currency });

            if (cart.item_count === 0) {
                this.body.innerHTML = '<div class="empty-cart-msg" style="padding:40px 20px; text-align:center; color:#94A3B8;">No protocols active in your cart.</div>';
                if (this.subtotal) this.subtotal.textContent = formatter.format(0);
                return;
            }

            let html = '';
            cart.items.forEach(item => {
                html += `
                <div class="cart-item-row" data-key="${item.key}">
                    <img src="${item.image}" alt="${item.title}" class="cart-item-img">
                    <div class="cart-item-details">
                        <div class="cart-item-title">${item.product_title}</div>
                        <div class="cart-item-price">${formatter.format(item.price / 100)} (x${item.quantity})</div>
                        <div style="margin-top:5px;">
                          <button class="btn-sm btn-outline remove-item" style="padding: 2px 8px; font-size:0.75rem;" onclick="window.RenuexCart.removeItem('${item.key}')">Remove</button>
                        </div>
                    </div>
                </div>
                `;
            });

            this.body.innerHTML = html;
            if (this.subtotal) this.subtotal.textContent = formatter.format(cart.total_price / 100);
            document.getElementById('cart-count').textContent = cart.item_count;
        },

        removeItem: function (key) {
            fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: key, quantity: 0 })
            })
                .then(res => res.json())
                .then(cart => {
                    this.updateCartCount();
                    this.renderCart(cart);
                });
        },

        updateCartCount: function () {
            fetch('/cart.js')
                .then(response => response.json())
                .then(cart => {
                    const cartCount = document.getElementById('cart-count');
                    if (cartCount) {
                        cartCount.textContent = cart.item_count;
                        if (cart.item_count > 0) {
                            cartCount.style.display = 'flex';
                        } else {
                            cartCount.style.display = 'none';
                        }
                    }
                })
                .catch(error => console.error('Error updating cart:', error));
        },

        showNotification: function (message, type = 'success') {
            const notification = document.createElement('div');
            notification.className = `notification notification-${type}`;
            notification.textContent = message;
            notification.style.cssText = `
                position: fixed;
                bottom: 100px;
                right: 20px;
                background-color: ${type === 'success' ? '#0047AB' : '#ef4444'};
                color: white;
                padding: 20px 30px;
                border-radius: 12px;
                box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                z-index: 99999;
                font-weight: bold;
                animation: slideInRight 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
            `;

            document.body.appendChild(notification);

            setTimeout(() => {
                notification.style.opacity = '0';
                notification.style.transform = 'translateX(20px)';
                notification.style.transition = 'all 0.4s ease';
                setTimeout(() => notification.remove(), 400);
            }, 4000);
        }
    };

    // Ensure cart drawer checkout button is linked correctly
    document.addEventListener('click', (e) => {
        if (e.target.matches('.cart-checkout-btn')) {
            // If they are on a dev preview, /checkout might fail, so we log it
            console.log('Transitioning to clinical checkout...');
        }
    });

    // ============================================
    // PRODUCT IMAGE GALLERY
    // ============================================
    function initProductGallery() {
        const thumbnails = document.querySelectorAll('.product-thumbnails img');
        const mainImage = document.querySelector('.main-product-image img');

        if (!mainImage || thumbnails.length === 0) return;

        thumbnails.forEach(thumb => {
            thumb.addEventListener('click', function () {
                const newSrc = this.dataset.fullsize || this.src.replace('_200x', '_800x');
                mainImage.src = newSrc;

                // Update active state
                thumbnails.forEach(t => t.style.borderColor = 'transparent');
                this.style.borderColor = 'var(--color-accent)';
            });
        });
    }

    // ============================================
    // FAQ ACCORDION
    // ============================================
    function initFAQAccordion() {
        document.querySelectorAll('.faq-question').forEach(question => {
            question.addEventListener('click', function () {
                const item = this.parentElement;
                const wasActive = item.classList.contains('active');

                // Close all items
                document.querySelectorAll('.faq-item').forEach(i => {
                    i.classList.remove('active');
                });

                // Open clicked item if it wasn't active
                if (!wasActive) {
                    item.classList.add('active');
                }
            });
        });
    }

    // ============================================
    // SEARCH MODAL
    // ============================================
    function initSearchModal() {
        const searchModal = document.getElementById('search-modal');
        if (!searchModal) return;

        // Close on escape key
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && searchModal.style.display === 'flex') {
                searchModal.style.display = 'none';
            }
        });
    }

    // ============================================
    // EMAIL POPUP (Exit Intent)
    // ============================================
    function initEmailPopup() {
        let popupShown = false;
        const popup = document.getElementById('email-popup');

        if (!popup) return;

        // Show on exit intent
        document.addEventListener('mouseout', function (e) {
            if (!popupShown && e.clientY < 50) {
                popup.style.display = 'flex';
                popupShown = true;
                localStorage.setItem('emailPopupShown', 'true');
            }
        });

        // Close popup
        popup.addEventListener('click', function (e) {
            if (e.target === popup || e.target.classList.contains('close-popup')) {
                popup.style.display = 'none';
            }
        });
    }

    // ============================================
    // QUANTITY SELECTORS
    // ============================================
    function initQuantitySelectors() {
        document.querySelectorAll('.quantity-selector').forEach(selector => {
            const input = selector.querySelector('input[type="number"]');
            const decreaseBtn = selector.querySelector('.quantity-decrease');
            const increaseBtn = selector.querySelector('.quantity-increase');

            if (decreaseBtn) {
                decreaseBtn.addEventListener('click', () => {
                    const currentValue = parseInt(input.value);
                    if (currentValue > 1) {
                        input.value = currentValue - 1;
                        input.dispatchEvent(new Event('change'));
                    }
                });
            }

            if (increaseBtn) {
                increaseBtn.addEventListener('click', () => {
                    const currentValue = parseInt(input.value);
                    input.value = currentValue + 1;
                    input.dispatchEvent(new Event('change'));
                });
            }
        });
    }

    // ============================================
    // LAZY LOADING IMAGES
    // ============================================
    function initLazyLoading() {
        if ('IntersectionObserver' in window) {
            const imageObserver = new IntersectionObserver((entries, observer) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const img = entry.target;
                        img.src = img.dataset.src;
                        img.classList.remove('lazy');
                        imageObserver.unobserve(img);
                    }
                });
            });

            document.querySelectorAll('img.lazy').forEach(img => {
                imageObserver.observe(img);
            });
        }
    }

    // ============================================
    // MOBILE NAVIGATION
    // ============================================
    function initMobileNav() {
        const trigger = document.getElementById('mobile-menu-trigger');
        const drawer = document.getElementById('mobile-nav-drawer');
        const overlay = document.getElementById('drawer-overlay');
        const closeBtn = document.getElementById('close-drawer');

        if (!trigger || !drawer || !overlay) return;

        function openDrawer() {
            drawer.classList.add('active');
            overlay.classList.add('active');
            document.body.style.overflow = 'hidden'; // Prevent scroll
        }

        function closeDrawer() {
            drawer.classList.remove('active');
            overlay.classList.remove('active');
            document.body.style.overflow = ''; // Restore scroll
        }

        trigger.addEventListener('click', openDrawer);
        closeBtn.addEventListener('click', closeDrawer);
        overlay.addEventListener('click', closeDrawer);

        // Close drawer on link click
        drawer.querySelectorAll('a').forEach(link => {
            link.addEventListener('click', closeDrawer);
        });
    }

    // ============================================
    // APP-LEVEL PAGE TRANSITIONS
    // ============================================
    function initPageTransitions() {
        const overlay = document.getElementById('page-transition-overlay');
        if (!overlay) return;

        // Intro fade on page load
        overlay.classList.add('active');
        setTimeout(() => {
            overlay.classList.remove('active');
        }, 300);

        // Bind out-transition to internal links
        document.querySelectorAll('a').forEach(link => {
            if (link.hostname === window.location.hostname && !link.hash && !link.target && !link.hasAttribute('download')) {
                link.addEventListener('click', function (e) {
                    if (e.ctrlKey || e.metaKey) return; // Allow opening in new tab
                    e.preventDefault();
                    overlay.classList.add('active');
                    const targetHref = this.href;
                    setTimeout(() => {
                        window.location.href = targetHref;
                    }, 400); // Wait for animation
                });
            }
        });

        // Handle browser back cache issue
        window.addEventListener('pageshow', function (event) {
            if (event.persisted) {
                overlay.classList.remove('active');
            }
        });
    }

    // ============================================
    // MAGNETIC BUTTONS
    // ============================================
    function initMagneticButtons() {
        document.querySelectorAll('.btn-primary, .btn-solid, .pulse-clinical').forEach(btn => {
            btn.classList.add('magnetic-btn');

            btn.addEventListener('mousemove', function (e) {
                const rect = btn.getBoundingClientRect();
                const x = e.clientX - rect.left - rect.width / 2;
                const y = e.clientY - rect.top - rect.height / 2;
                btn.style.transform = `translate(${x * 0.2}px, ${y * 0.2}px)`;
            });

            btn.addEventListener('mouseleave', function () {
                btn.style.transform = 'translate(0px, 0px)';
            });
        });
    }

    // ============================================
    // PARALLAX EFFECT
    // ============================================
    function initParallax() {
        const parallaxEls = document.querySelectorAll('.science-hero img, .hero-premium img, .about-laboratory img');
        if (!parallaxEls.length) return;

        parallaxEls.forEach(el => {
            el.parentElement.classList.add('parallax-container');
            el.classList.add('parallax-img');
        });

        window.addEventListener('scroll', () => {
            if (window.innerWidth < 768) return; // Disable on very small screens for perf
            parallaxEls.forEach(el => {
                const speed = 0.2;
                const top = el.parentElement.getBoundingClientRect().top;
                if (top < window.innerHeight && top > -el.parentElement.offsetHeight) {
                    el.style.transform = `translateY(${top * speed}px)`;
                }
            });
        });
    }

    // ============================================
    // INITIALIZE ALL
    // ============================================
    function init() {
        initStickyHeader();
        initMobileNav();
        initSmoothScroll();
        initScrollAnimations();
        Cart.init();
        initProductGallery();
        initFAQAccordion();
        initSearchModal();
        initQuantitySelectors();
        initLazyLoading();

        // Elite Level Interactions
        initPageTransitions();
        initMagneticButtons();
        initParallax();
    }

    // Run on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Expose Cart to global scope for external use
    window.RenuexCart = Cart;

})();

// ============================================
// ANIMATIONS
// ============================================
const style = document.createElement('style');
style.textContent = `
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideOutRight {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(100%);
      opacity: 0;
    }
  }
`;
document.head.appendChild(style);
